import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCode, FiEye, FiUpload, FiX } from "react-icons/fi";
import api, { apiError } from "../../../lib/api";
import { buildCanonicalUrl } from "../../../lib/seo";
import RenderToast from "../../../appcomponents/RenderToast";
import CustomQuillEditor from "../../../components/CustomQuillEditor";
import { sanitizeBlogHtml } from "../../../utils/blogHtml";
import {
  createBlogAutoSchema,
  formatHtmlForCodeEditor,
  normalizeEditorCodeHtml,
  normalizeSchemaMarkup,
} from "../../../utils/blogSchema";
import styles from "./AddEditBlogModal.module.css";

const emptyForm = {
  title: "",
  excerpt: "",
  content: "",
  status_id: "2",
  meta_title: "",
  meta_description: "",
  schema_markup: "",
  canonical_url: "",
  slug: "",
  published_at: "",
};

const chipId = (value) => String(value);
const schemaTabs = {
  auto: "auto",
  custom: "custom",
};
const DRAFT_STORAGE_PREFIX = "psprcm.blogDraft";

const slugify = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const getDraftStorageKey = (blogId) =>
  `${DRAFT_STORAGE_PREFIX}:${blogId ? `blog-${blogId}` : "new"}`;

const readDraftState = (storageKey) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeDraftState = (storageKey, payload) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // Ignore storage quota / privacy mode issues.
  }
};

const clearDraftState = (storageKey) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // Ignore storage failures.
  }
};

export default function AddEditBlogModal({
  open,
  blog = null,
  statuses = [],
  categories = [],
  tags = [],
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm);
  const [categoryIds, setCategoryIds] = useState([]);
  const [tagIds, setTagIds] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageError, setImageError] = useState(false);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editorTab, setEditorTab] = useState("visual");
  const [editorContent, setEditorContent] = useState("");
  const [schemaTab, setSchemaTab] = useState(schemaTabs.auto);
  const imageInputRef = useRef(null);
  const initialSnapshotRef = useRef(null);
  const slugTouchedRef = useRef(false);
  const canonicalTouchedRef = useRef(false);
  const draftHydratedRef = useRef(false);
  const lastAutoSlugRef = useRef("");
  const lastAutoCanonicalRef = useRef("");
  const draftStorageKey = getDraftStorageKey(blog?.id);

  useEffect(() => {
    if (!open) {
      draftHydratedRef.current = false;
      return;
    }

    const nextForm = blog
      ? {
          title: blog.title || "",
          excerpt: blog.excerpt || "",
          content: blog.content || "",
          status_id: String(blog.status_id || "2"),
          meta_title: blog.meta_title || "",
          meta_description: blog.meta_description || "",
          schema_markup: blog.schema_markup || "",
          canonical_url: blog.canonical_url || "",
          slug: blog.slug || "",
          published_at: blog.published_at
            ? String(blog.published_at).slice(0, 16)
            : "",
        }
      : emptyForm;

    const nextCategoryIds = (blog?.categories || []).map((item) => item.id);
    const nextTagIds = (blog?.tags || []).map((item) => item.id);
    const draftState = readDraftState(draftStorageKey);
    const savedForm = draftState?.form || nextForm;
    const savedCategoryIds = Array.isArray(draftState?.categoryIds)
      ? draftState.categoryIds
      : nextCategoryIds;
    const savedTagIds = Array.isArray(draftState?.tagIds)
      ? draftState.tagIds
      : nextTagIds;
    const savedEditorContent = normalizeEditorCodeHtml(
      draftState?.editorContent ?? savedForm.content ?? "",
    );
    const savedSchemaTab =
      draftState?.schemaTab === schemaTabs.custom
        ? schemaTabs.custom
        : schemaTabs.auto;

    setForm(savedForm);
    setCategoryIds(savedCategoryIds);
    setTagIds(savedTagIds);
    setImagePreview(
      typeof draftState?.imagePreview === "string"
        ? draftState.imagePreview
        : blog?.image_url || "",
    );
    setImageError(false);
    if (!draftState) {
      setImageFile(null);
    }
    setImageRemoved(Boolean(draftState?.imageRemoved));
    setEditorTab(draftState?.editorTab === "code" ? "code" : "visual");
    setEditorContent(savedEditorContent);
    setSchemaTab(savedSchemaTab);

    const autoSlug = slugify(nextForm.title);
    const isSlugAuto = !blog?.id || !blog?.slug || blog.slug === autoSlug;
    const autoCanonical = blog?.slug
      ? buildCanonicalUrl(`/blog/${blog.slug}`)
      : autoSlug
        ? buildCanonicalUrl(`/blog/${autoSlug}`)
        : "";
    slugTouchedRef.current =
      typeof draftState?.slugTouched === "boolean"
        ? draftState.slugTouched
        : !isSlugAuto;
    canonicalTouchedRef.current =
      typeof draftState?.canonicalTouched === "boolean"
        ? draftState.canonicalTouched
        : Boolean(blog?.canonical_url && blog.canonical_url !== autoCanonical);
    lastAutoSlugRef.current = autoSlug;
    lastAutoCanonicalRef.current = autoSlug
      ? buildCanonicalUrl(`/blog/${autoSlug}`)
      : "";
    initialSnapshotRef.current = {
      title: savedForm.title || "",
      excerpt: savedForm.excerpt || "",
      content: sanitizeBlogHtml(savedEditorContent),
      status_id: savedForm.status_id || "2",
      meta_title: savedForm.meta_title || "",
      meta_description: savedForm.meta_description || "",
      schema_markup: savedForm.schema_markup || "",
      canonical_url: savedForm.canonical_url || "",
      slug: savedForm.slug || "",
      published_at: savedForm.published_at || "",
      category_ids: savedCategoryIds,
      tag_ids: savedTagIds,
    };
    draftHydratedRef.current = true;
  }, [open, blog, draftStorageKey]);

  useEffect(() => {
    if (!imageFile) return;
    setUploading(true);
    const previewUrl = URL.createObjectURL(imageFile);
    setImagePreview(previewUrl);
    RenderToast({ type: "success", message: "Image attached successfully." });
    const timer = window.setTimeout(() => setUploading(false), 350);
    return () => {
      URL.revokeObjectURL(previewUrl);
      window.clearTimeout(timer);
    };
  }, [imageFile]);

  useEffect(() => {
    if (!open || !draftHydratedRef.current) return;
    writeDraftState(draftStorageKey, {
      form,
      categoryIds,
      tagIds,
      editorContent,
      editorTab,
      schemaTab,
      imageRemoved,
      slugTouched: slugTouchedRef.current,
      canonicalTouched: canonicalTouchedRef.current,
    });
  }, [
    open,
    draftStorageKey,
    form,
    categoryIds,
    tagIds,
    editorContent,
    editorTab,
    schemaTab,
    imageRemoved,
  ]);

  useEffect(() => {
    if (!open) return;
    setForm((prev) =>
      prev.content === editorContent
        ? prev
        : { ...prev, content: editorContent },
    );
  }, [editorContent, open]);

  const autoSchema = useMemo(
    () =>
      createBlogAutoSchema({
        title: form.title,
        excerpt: form.excerpt,
        metaDescription: form.meta_description,
        image:
          imageRemoved
            ? ""
            : imagePreview && /^https?:\/\//i.test(imagePreview)
              ? imagePreview
              : blog?.image_url || "",
        publishedAt: form.published_at,
        slug: form.slug || form.title,
        canonicalUrl: form.canonical_url,
      }),
    [
      blog?.image_url,
      form.canonical_url,
      form.excerpt,
      form.meta_description,
      form.published_at,
      form.slug,
      form.title,
      imageRemoved,
      imagePreview,
    ],
  );
  const autoSchemaJson = useMemo(
    () => JSON.stringify(autoSchema, null, 2),
    [autoSchema],
  );
  const customSchemaValue = normalizeSchemaMarkup(form.schema_markup);

  const slugSource = useMemo(
    () => form.slug || form.title,
    [form.slug, form.title],
  );

  const updateField = (name, value) => {
    if (name === "title") {
      setForm((prev) => {
        const nextTitle = value;
        const nextSlug = slugify(nextTitle);
        const currentSlug = prev.slug || "";
        const currentCanonical = prev.canonical_url || "";
        const previousAutoSlug = lastAutoSlugRef.current || slugify(prev.title);
        const previousAutoCanonical = lastAutoCanonicalRef.current || buildCanonicalUrl(`/blog/${previousAutoSlug}`);
        const shouldSyncSlug =
          !slugTouchedRef.current || currentSlug === previousAutoSlug || currentSlug === slugify(prev.title);
        const shouldSyncCanonical =
          !canonicalTouchedRef.current || currentCanonical === previousAutoCanonical;

        const next = { ...prev, title: nextTitle };
        if (shouldSyncSlug) {
          next.slug = nextSlug;
          lastAutoSlugRef.current = nextSlug;
        }
        if (shouldSyncCanonical) {
          const canonicalForSlug = nextSlug ? buildCanonicalUrl(`/blog/${nextSlug}`) : "";
          next.canonical_url = canonicalForSlug;
          lastAutoCanonicalRef.current = canonicalForSlug;
        }
        return next;
      });
      return;
    }

    if (name === "slug") {
      slugTouchedRef.current = true;
      setForm((prev) => {
        const nextSlug = slugify(value);
        const next = { ...prev, slug: value };
        if (!canonicalTouchedRef.current || prev.canonical_url === lastAutoCanonicalRef.current) {
          const canonicalForSlug = nextSlug ? buildCanonicalUrl(`/blog/${nextSlug}`) : "";
          next.canonical_url = canonicalForSlug;
          lastAutoCanonicalRef.current = canonicalForSlug;
        }
        lastAutoSlugRef.current = nextSlug;
        return next;
      });
      return;
    }

    if (name === "canonical_url") {
      canonicalTouchedRef.current = true;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageError(false);
      setImageRemoved(false);
      setImageFile(file);
    }
  };

  const handleQuillImageUpload = async (file) => {
    const data = new FormData();
    data.append("action", "upload_image");
    if (blog?.id) {
      data.append("blog_id", blog.id);
    }
    data.append("base_name", slugSource || form.title || "blog-image");
    data.append("image", file);

    const response = await api.post("/api/blogs.php", data);
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Unable to upload image.");
    }

    return response.data?.data;
  };

  const toggleId = (setter, list, value, label) => {
    const key = Number(value);
    if (list.includes(key)) {
      RenderToast({
        type: "warning",
        message: `${label} is already selected.`,
      });
      return;
    }

    setter([...list, key]);
    RenderToast({ type: "success", message: `${label} added.` });
  };

  const removeChip = (setter, list, value, label) => {
    setter(list.filter((item) => item !== Number(value)));
    RenderToast({ type: "success", message: `${label} removed.` });
  };

  const schemaPreviewLabel =
    schemaTab === schemaTabs.auto
      ? "Auto schema updates with your blog fields."
      : customSchemaValue
        ? "Custom schema will be used as-is."
        : "Leave custom schema blank to use auto schema.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      RenderToast({ type: "error", message: "Blog title is required." });
      return;
    }

    setSaving(true);
    try {
      const contentToSave = editorContent;
      const sanitizedContent = sanitizeBlogHtml(contentToSave);
      const normalizedCurrent = {
        title: form.title,
        excerpt: form.excerpt,
        content: sanitizedContent,
        status_id: form.status_id,
        meta_title: form.meta_title,
        meta_description: form.meta_description,
        schema_markup: schemaTab === schemaTabs.custom ? customSchemaValue : "",
        canonical_url: form.canonical_url,
        slug: form.slug || slugSource,
        published_at: form.published_at,
        category_ids: categoryIds,
        tag_ids: tagIds,
      };
      const initialSnapshot = initialSnapshotRef.current;
      const onlyStatusChanged =
        blog &&
        initialSnapshot &&
        JSON.stringify({
          ...normalizedCurrent,
          status_id: initialSnapshot.status_id,
        }) === JSON.stringify(initialSnapshot) &&
        normalizedCurrent.status_id !== initialSnapshot.status_id;

      const data = new FormData();
      data.append("action", blog ? "update" : "add");
      if (blog?.id) data.append("id", blog.id);
      data.append("title", form.title);
      data.append("excerpt", form.excerpt);
      data.append("content", sanitizedContent);
      data.append("status_id", form.status_id);
      data.append("meta_title", form.meta_title);
      data.append("meta_description", form.meta_description);
      data.append(
        "schema_markup",
        schemaTab === schemaTabs.custom ? customSchemaValue : "",
      );
      data.append("canonical_url", form.canonical_url);
      data.append("slug", form.slug || slugSource);
      data.append("published_at", form.published_at);
      data.append("category_ids", JSON.stringify(categoryIds));
      data.append("tag_ids", JSON.stringify(tagIds));
      if (imageFile) data.append("image", imageFile);
      if (imageRemoved && !imageFile) data.append("remove_image", "1");

      const response = await api.post("/api/blogs.php", data);

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to save blog.");
      }

      RenderToast({
        type: "success",
        message: blog
          ? onlyStatusChanged
            ? "Blog status updated successfully."
            : response.data?.message || "Blog updated successfully."
          : response.data?.message || "Blog added successfully.",
      });
      clearDraftState(draftStorageKey);
      onSaved?.();
      onClose?.();
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <p className={styles.kicker}>{blog ? "Edit Blog" : "Add Blog"}</p>
            <h3>{blog ? "Update blog" : "Create a blog post"}</h3>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            <FiX />
          </button>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.grid}>
            <label className={styles.fieldFull}>
              <span>Blog Title</span>
              <input
                type="text"
                value={form.title}
                onChange={(e) => updateField("title", e.target.value)}
                placeholder="Enter blog title"
              />
            </label>

            <label className={styles.fieldFull}>
              <span>Excerpt</span>
              <textarea
                rows={3}
                value={form.excerpt}
                onChange={(e) => updateField("excerpt", e.target.value)}
                placeholder="Short summary used in blog cards and banner"
              />
            </label>

            <div className={`${styles.fieldFull} ${styles.editorField}`}>
              <div className={styles.editorHead}>
                <span>Blog Content / Description</span>
                <div className={styles.editorTabs}>
                  <button
                    type="button"
                    className={editorTab === "visual" ? styles.tabActive : ""}
                    onClick={() => {
                      setEditorTab("visual");
                    }}
                  >
                    <FiEye /> Visual
                  </button>
                  <button
                    type="button"
                    className={editorTab === "code" ? styles.tabActive : ""}
                    onClick={() => {
                      setEditorTab("code");
                      setEditorContent((prev) =>
                        formatHtmlForCodeEditor(normalizeEditorCodeHtml(prev)),
                      );
                    }}
                  >
                    <FiCode /> Code
                  </button>
                </div>
              </div>
              <div className={styles.editorWrap}>
                {editorTab === "visual" ? (
                  <CustomQuillEditor
                    value={editorContent}
                    onChange={(value) =>
                      setEditorContent(normalizeEditorCodeHtml(sanitizeBlogHtml(value)))
                    }
                    onImageUpload={handleQuillImageUpload}
                    placeholder="Write blog content..."
                  />
                ) : (
                  <textarea
                    className={styles.codeEditor}
                    value={editorContent}
                    onChange={(e) => setEditorContent(e.target.value)}
                    placeholder="Write or paste HTML code..."
                  />
                )}
              </div>
            </div>

            <label>
              <span>Status</span>
              <select
                value={form.status_id}
                onChange={(e) => updateField("status_id", e.target.value)}
              >
                {(statuses.length
                  ? statuses
                  : [
                      { id: 1, name: "Publish" },
                      { id: 2, name: "Draft" },
                      { id: 3, name: "Schedule" },
                    ]
                ).map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Canonical URL</span>
              <input
                type="text"
                value={form.canonical_url}
                onChange={(e) => updateField("canonical_url", e.target.value)}
                placeholder="https://example.com/blog"
              />
            </label>

            <label>
              <span>Meta Title</span>
              <input
                type="text"
                value={form.meta_title}
                onChange={(e) => updateField("meta_title", e.target.value)}
                placeholder="SEO meta title"
              />
            </label>

            <label>
              <span>Custom URL / Slug</span>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => updateField("slug", e.target.value)}
                placeholder="custom-url"
              />
            </label>

            <label className={styles.fieldFull}>
              <span>Meta Description</span>
              <textarea
                value={form.meta_description}
                onChange={(e) =>
                  updateField("meta_description", e.target.value)
                }
                rows={3}
                placeholder="SEO meta description"
              />
            </label>

            <div className={`${styles.fieldFull} ${styles.schemaField}`}>
              <div className={styles.schemaHead}>
                <span>Schema</span>
                <div className={styles.schemaTabs}>
                  <button
                    type="button"
                    className={
                      schemaTab === schemaTabs.auto ? styles.tabActive : ""
                    }
                    onClick={() => setSchemaTab(schemaTabs.auto)}
                  >
                    Auto Schema
                  </button>
                  <button
                    type="button"
                    className={
                      schemaTab === schemaTabs.custom ? styles.tabActive : ""
                    }
                    onClick={() => setSchemaTab(schemaTabs.custom)}
                  >
                    Custom Schema
                  </button>
                </div>
              </div>
              {schemaTab === schemaTabs.auto ? (
                <textarea
                  className={styles.codeEditor}
                  value={autoSchemaJson}
                  readOnly
                  rows={10}
                />
              ) : (
                <textarea
                  value={form.schema_markup}
                  onChange={(e) => updateField("schema_markup", e.target.value)}
                  rows={10}
                  placeholder="Leave blank to use auto schema"
                />
              )}
              <small className={styles.helperText}>{schemaPreviewLabel}</small>
              <span
                className={
                  schemaTab === schemaTabs.auto
                    ? styles.schemaBadge
                    : styles.schemaBadgeCustom
                }
              >
                {schemaTab === schemaTabs.auto
                  ? "Auto schema active"
                  : customSchemaValue
                    ? "Custom schema active"
                    : "Custom schema empty"}
              </span>
            </div>

            <label>
              <span>Publish Date</span>
              <input
                type="datetime-local"
                value={form.published_at}
                onChange={(e) => updateField("published_at", e.target.value)}
              />
            </label>

            <div>
              <span className={styles.label}>Category</span>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    const category = categories.find(
                      (item) => Number(item.id) === Number(e.target.value),
                    );
                    toggleId(
                      setCategoryIds,
                      categoryIds,
                      e.target.value,
                      category?.title || "Category",
                    );
                  }
                  e.target.value = "";
                }}
              >
                <option value="">Select category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
              </select>
              <div className={styles.chips}>
                {categoryIds.map((id) => {
                  const item = categories.find(
                    (entry) => Number(entry.id) === Number(id),
                  );
                  if (!item) return null;
                  return (
                    <button
                      type="button"
                      key={chipId(id)}
                      className={styles.chip}
                      onClick={() =>
                        removeChip(setCategoryIds, categoryIds, id, item.title)
                      }
                    >
                      {item.title} <FiX />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <span className={styles.label}>Tags</span>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    const tag = tags.find(
                      (item) => Number(item.id) === Number(e.target.value),
                    );
                    toggleId(
                      setTagIds,
                      tagIds,
                      e.target.value,
                      tag?.title || "Tag",
                    );
                  }
                  e.target.value = "";
                }}
              >
                <option value="">Select tag</option>
                {tags.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.title}
                  </option>
                ))}
              </select>
              <div className={styles.chips}>
                {tagIds.map((id) => {
                  const item = tags.find(
                    (entry) => Number(entry.id) === Number(id),
                  );
                  if (!item) return null;
                  return (
                    <button
                      type="button"
                      key={chipId(id)}
                      className={styles.chip}
                      onClick={() =>
                        removeChip(setTagIds, tagIds, id, item.title)
                      }
                    >
                      {item.title} <FiX />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.uploadBox}>
              <span className={styles.label}>Blog Image</span>
              <div
                className={`${styles.preview} ${imagePreview && !imageError ? styles.previewHasImage : styles.previewEmpty}`}
                onClick={() => imageInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    imageInputRef.current?.click();
                  }
                }}
              >
                {uploading ? (
                  <div className={styles.spinner} />
                ) : imagePreview && !imageError ? (
                  <img
                    src={imagePreview}
                    alt="Blog preview"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className={styles.emptyPreview}>
                    <span className={styles.emptyPreviewIcon}>
                      <FiUpload />
                    </span>
                    <span>Upload image</span>
                  </div>
                )}
                {/* <div className={styles.previewOverlay}>
                  <span className={styles.previewOverlayIcon}>
                    <FiUpload />
                  </span>
                  <span>Upload image</span>
                </div> */}
                {imagePreview && !imageError ? (
                  <button
                    type="button"
                    className={styles.removeImageBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setImagePreview("");
                      setImageFile(null);
                      setImageRemoved(true);
                      RenderToast({
                        type: "success",
                        message: "Image removed.",
                      });
                    }}
                  >
                    <FiX />
                  </button>
                ) : null}
              </div>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImage}
                className={styles.hiddenFile}
              />
            </div>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={saving || uploading}
            >
              {saving ? "Saving..." : blog ? "Update Blog" : "Add Blog"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
